import {
  sortWith,
  sortBy,
  ascend,
  prop,
  compose,
  toLower,
  equals,
  pick,
} from 'ramda';
import {
  DRIVER_TYPES,
  EDITABLE_ROUTE_STATUSES,
  EMPTY_DRIVER,
  EMPTY_ODF_DRIVER,
} from './constants';
import { fromMinutesToTime, toMinutes } from '../../utils/date';
import { joinArrayToString } from '../../utils/string';

export const isRouteEditable = ({ routeStatus }) =>
  EDITABLE_ROUTE_STATUSES.includes(routeStatus);

export const isRouteReadOnly = (route) => !isRouteEditable(route);

export const isRouteDriverSelected = (driver) => driver && driver.driverCode;

export const isRouteDriverChanged = (route, driver, odfDriver, drivers) => {
  const initialOdfDriver = getRouteOdfDriver(drivers, route);
  return (
    route.driverCode !== driver?.driverCode ||
    (!initialOdfDriver && !isEmptyOdfDriver(odfDriver)) ||
    (initialOdfDriver && initialOdfDriver !== odfDriver?.driverCode)
  );
};

export const getDriverByCode = (
  drivers = [],
  driverCode,
  defaultValue = EMPTY_DRIVER
) => drivers.find((driver) => driver.driverCode === driverCode) || defaultValue;

export const getRouteOdfDriver = (drivers, { odfRouteOwner, driverCode }) => {
  const selectedDriver = getDriverByCode(drivers, driverCode);
  if (odfRouteOwner) {
    return odfRouteOwner;
  }
  return selectedDriver.driverType === DRIVER_TYPES.ODF ? driverCode : '';
};

export const getSectionByCode = (sections = [], sectionCode) =>
  sections.find(
    (section) => section.sectionCode.toString() === sectionCode.toString()
  );

export const getDriverData = (drivers, { driverCode }, section) => ({
  ...getDriverByCode(drivers, driverCode),
  ...(section ? section : []),
});

export const getActiveDays = (days) =>
  days
    .filter((day) => day.active)
    .map((day) => day.dayOfWeek)
    .join(', ');

export const driverNameComparator = ascend(
  compose(toLower, prop('driverName'))
);

export const isEmptyOdfDriver = (driver) => equals(driver, EMPTY_ODF_DRIVER);

export const getInitialDriverDetailsValues = (driver, odfDriver, sections) => {
  const sourceDriver = !isEmptyOdfDriver(odfDriver) ? odfDriver : driver;
  const sectionCode =
    (sourceDriver?.sectionCode &&
      getSectionByCode(sections, sourceDriver.sectionCode)?.sectionCode) ||
    '';

  return {
    ...sourceDriver,
    ...pick(
      [
        'displayName',
        'deliveryStopTime',
        'collectionStopTime',
        'driverCode',
        'driverType',
        'driverName',
        'password',
      ],
      driver
    ),
    sectionCode,
  };
};

const getOdfDrivers = (drivers = []) =>
  drivers.filter(({ driverType = '' }) => driverType === DRIVER_TYPES.ODF);

export const getSortedOdfDriversList = (drivers = []) => {
  const odfDrivers = getOdfDrivers(drivers).map((driver) => {
    const shortDriverCode = driver.driverCode.split('*')[1];
    return {
      value: driver.driverCode,
      label: `${driver.driverName.slice(0, 32)} (${shortDriverCode})`,
      driverName: driver.driverName,
    };
  });
  let sortedOdfDrivers = sortBy(driverNameComparator)(odfDrivers);
  sortedOdfDrivers.unshift(EMPTY_ODF_DRIVER);
  return sortedOdfDrivers;
};

export const getFormattedDriversList = (drivers = [], odfDriver) => {
  const odfDriverSelected = !isEmptyOdfDriver(odfDriver);
  return getSortedDrivers(
    drivers.filter(({ driverType = '', driverCode }) =>
      odfDriverSelected
        ? driverType === DRIVER_TYPES.LINKED ||
          driverCode === odfDriver.driverCode
        : driverType !== DRIVER_TYPES.LINKED && driverType !== DRIVER_TYPES.ODF
    )
  );
};

export const getSortedDrivers = (drivers = []) => {
  const formattedDrivers = drivers.map((driver) => {
    const isActive = driver.driverActive;
    const workingDays = getActiveDays(driver.activeDays);
    const assignedRoute = driver.assignedRouteCode || '###';
    const shortDriverCode = driver.driverCode.split('*')[1];
    return {
      value: driver.driverCode,
      label: joinArrayToString(
        [
          `(${shortDriverCode})`,
          assignedRoute,
          driver.driverName.slice(0, 32),
          workingDays,
        ],
        ' - '
      ),
      driverName: driver.driverName,
      assignedRouteCode: driver.assignedRouteCode,
      isActive,
      driverType: driver.driverType,
    };
  });

  const routeCodeSort = sortWith([
    ascend(({ assignedRouteCode }) => !!assignedRouteCode),
    driverNameComparator,
  ]);

  const mainDrivers = routeCodeSort(
    formattedDrivers.filter((driver) => driver.isActive)
  );

  const otherDrivers = sortBy(driverNameComparator)(
    formattedDrivers.filter((driver) => !driver.isActive)
  );

  return [...mainDrivers, ...otherDrivers];
};

export const mapDriverForFormModel = (driver) => ({
  ...driver,
  deliveryStopTime: fromMinutesToTime(driver.deliveryStopTime),
  collectionStopTime: fromMinutesToTime(driver.collectionStopTime),
});

export const mapDriverForServer = ({ driver, driverCode }) => ({
  driverName: driver.driverName.trim(),
  displayName: driver.displayName,
  vehicleTypeCode: driver.vehicleTypeCode,
  dutyStartTime: driver.dutyStartTime,
  dutyEndTime: driver.dutyEndTime,
  plannedDepartTime: driver.plannedDepartTime,
  plannedReturnTime: driver.plannedReturnTime,
  deliveryStopTime: toMinutes(driver.deliveryStopTime),
  collectionStopTime: toMinutes(driver.collectionStopTime),
  saturnLockdown: !!driver.saturnLockdown,
  startAtSatellite: !!driver.startAtSatellite,
  endAtSatellite: !!driver.endAtSatellite,
  sectionCode: driver.sectionCode,
  odfRouteOwner: driver.odfRouteOwner,
  ...(driverCode && { driverCode }),
});

export const getDriverIdFromDriverCode = (driverCode) =>
  driverCode.split('*')[1];
